import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, PencilSimple, Trash, Bank, Buildings, Star } from '@phosphor-icons/react';

export default function MasterSettingsPage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Master Settings</h1>
      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company">Company Details</TabsTrigger>
          <TabsTrigger value="bank">Bank Details</TabsTrigger>
        </TabsList>
        <TabsContent value="company"><CompanyDetails /></TabsContent>
        <TabsContent value="bank"><BankDetails /></TabsContent>
      </Tabs>
    </div>
  );
}

function CompanyDetails() {
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', gstin: '', dl_number: '', food_regi_no: '', pan_number: '', state: '', state_code: '', logo_url: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.getCompanyDetails().then(r => { if (r.data) setForm(prev => ({ ...prev, ...r.data })); }); }, []);

  const save = async () => {
    setSaving(true); setSaved(false);
    try { await api.updateCompanyDetails(form); setSaved(true); setTimeout(() => setSaved(false), 3000); } catch {}
    setSaving(false);
  };

  return (
    <div className="mt-4 max-w-2xl space-y-4">
      <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div className="flex items-center gap-2 mb-4">
          <Buildings size={20} weight="duotone" style={{ color: '#007AFF' }} />
          <h3 className="text-sm font-bold" style={{ color: '#0A2540' }}>Business Information</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: 'name', l: 'Company Name' }, { k: 'address', l: 'Address' },
            { k: 'phone', l: 'Phone' }, { k: 'email', l: 'Email' },
            { k: 'gstin', l: 'GSTIN' }, { k: 'dl_number', l: 'Drug License No.' },
            { k: 'food_regi_no', l: 'Food Registration No.' }, { k: 'pan_number', l: 'PAN Number' },
            { k: 'state', l: 'State' }, { k: 'state_code', l: 'State Code' },
          ].map(f => (
            <div key={f.k} className={f.k === 'address' ? 'col-span-2' : ''}>
              <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.l}</Label>
              <Input value={form[f.k] || ''} onChange={e => setForm(fo => ({ ...fo, [f.k]: e.target.value }))} className="h-8 mt-1 text-sm" />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={save} disabled={saving} className="h-9 px-6 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          {saved && <span className="text-xs font-medium" style={{ color: '#34C759' }}>Saved successfully!</span>}
        </div>
      </div>
      <p className="text-xs" style={{ color: '#8A94A6' }}>These details will appear on invoices, reports, and other documents.</p>
    </div>
  );
}

function BankDetails() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBank, setEditBank] = useState(null);

  const load = () => { api.getBankDetails().then(r => { setBanks(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const deleteBank = async (id) => {
    if (window.confirm('Delete this bank account?')) { await api.deleteBankDetail(id); load(); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm" style={{ color: '#8A94A6' }}>{banks.length} bank account(s) configured</p>
        <Button onClick={() => { setEditBank(null); setShowForm(true); }} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} className="mr-1" /> Add Bank
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? <p style={{ color: '#8A94A6' }}>Loading...</p> :
          banks.map(b => (
            <div key={b._id} className="p-4 rounded-md border relative" style={{ background: '#FFFFFF', borderColor: b.is_default ? '#007AFF' : '#E2E4E9', borderWidth: b.is_default ? 2 : 1 }}>
              {b.is_default && (
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: '#007AFF10', color: '#007AFF' }}>
                  <Star size={10} weight="fill" className="inline mr-0.5" /> Default
                </span>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Bank size={18} weight="duotone" style={{ color: '#007AFF' }} />
                <span className="text-sm font-bold" style={{ color: '#0A2540' }}>{b.bank_name}</span>
              </div>
              <div className="space-y-1 text-xs" style={{ color: '#8A94A6' }}>
                <p>A/C Holder: <strong style={{ color: '#0A2540' }}>{b.account_holder}</strong></p>
                <p>A/C No.: <strong style={{ color: '#0A2540' }}>{b.account_number}</strong></p>
                <p>IFSC: <strong style={{ color: '#0A2540' }}>{b.ifsc_code}</strong></p>
                <p>Branch: {b.branch_name}</p>
                {b.upi_id && <p>UPI: {b.upi_id}</p>}
              </div>
              <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid #E2E4E9' }}>
                <button onClick={() => { setEditBank(b); setShowForm(true); }} className="text-xs font-medium" style={{ color: '#007AFF' }}>
                  <PencilSimple size={12} className="inline mr-0.5" /> Edit
                </button>
                <button onClick={() => deleteBank(b._id)} className="text-xs font-medium" style={{ color: '#FF3B30' }}>
                  <Trash size={12} className="inline mr-0.5" /> Delete
                </button>
              </div>
            </div>
          ))
        }
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editBank ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle></DialogHeader>
          <BankForm bank={editBank} onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BankForm({ bank, onSave }) {
  const [form, setForm] = useState(bank || { bank_name: '', account_holder: '', account_number: '', ifsc_code: '', branch_name: '', upi_id: '', qr_code: '', is_default: false });
  const save = async () => {
    try {
      if (bank?._id) await api.updateBankDetail(bank._id, form);
      else await api.createBankDetail(form);
      onSave();
    } catch {}
  };
  return (
    <div className="space-y-3">
      {[
        { k: 'bank_name', l: 'Bank Name' }, { k: 'account_holder', l: 'Account Holder Name' },
        { k: 'account_number', l: 'Account Number' }, { k: 'ifsc_code', l: 'IFSC Code' },
        { k: 'branch_name', l: 'Branch Name' }, { k: 'upi_id', l: 'UPI ID (Optional)' },
      ].map(f => (
        <div key={f.k}><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.l}</Label>
          <Input value={form[f.k] || ''} onChange={e => setForm(fo => ({ ...fo, [f.k]: e.target.value }))} className="h-8 mt-1 text-sm" /></div>
      ))}
      <div className="flex items-center justify-between p-2 rounded" style={{ background: '#F5F6F8' }}>
        <Label className="text-xs font-medium" style={{ color: '#0A2540' }}>Set as Default Bank</Label>
        <Switch checked={form.is_default} onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))} />
      </div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save Bank Account</Button>
    </div>
  );
}
